import {
  ReactNode,
  memo,
  useMemo,
  MouseEvent,
  CSSProperties,
  forwardRef,
  KeyboardEvent,
  TouchEvent,
} from "react";
import "./button.scss";
import classNames from "classnames";
import { ButtonLoadingIcon } from "@components/icons";

export type ButtonVariant =
  | "primary"
  | "success"
  | "default"
  | "danger"
  | "info"
  | "warning"
  | "close"
  | "icon"
  | "transparent"
  | "cancel"
  | "default-bordered"
  | "success-bordered"
  | "link";
interface ButtonProps {
  variant?: ButtonVariant;
  className?: string;
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
  onMouseEnter?: (e: MouseEvent<HTMLButtonElement>) => void;
  onMouseLeave?: (e: MouseEvent<HTMLButtonElement>) => void;
  type?: "button" | "submit" | "reset";
  children?: ReactNode;
  disabled?: boolean;
  style?: CSSProperties;
  loading?: boolean;
  height?: number;
  loaderHeight?: number;
  loaderColor?: string;
  size?: "medium" | "small" | "large" | "x-large" | "mobile-small";
  onKeyDown?: (e: KeyboardEvent<HTMLButtonElement>) => void;
  onMouseDown?: (e: MouseEvent<HTMLButtonElement>) => void;
  onTouchStart?: (e: TouchEvent<HTMLButtonElement>) => void;
  role?: string;
  // tabIndex?: number;
}
type Ref = HTMLButtonElement;
const ButtonComponent = forwardRef<Ref, ButtonProps>(
  (
    {
      className,
      type = "button",
      variant = "default",
      children,
      loading = false,
      disabled = false,
      loaderHeight,
      loaderColor,
      size,
      ...restProps
    },
    ref,
  ) => {
    const buttonClassName = useMemo<string>(() => {
      switch (variant) {
        case "primary":
          return "primary-button";
        case "success":
          return "success-button";
        case "danger":
          return "danger-button";
        case "warning":
          return "warning-button";
        case "cancel":
          return "cancel-button";
        case "info":
          return "info-button";
        case "success-bordered":
          return "success-bordered-button";
        case "icon":
          return "icon-button";

        case "close":
          return "close-button";

        case "transparent":
          return "transparent-button";

        case "default-bordered":
          return "default-bordered-button";

        case "link":
          return "link";

        default:
          return "default-button";
      }
    }, [variant]);
    return (
      <button
        className={classNames("button", buttonClassName, size, className)}
        type={type}
        disabled={disabled || loading}
        ref={ref}
        {...restProps}
      >
        {loading ? (
          <ButtonLoadingIcon height={loaderHeight} color={loaderColor} />
        ) : (
          <span className="content">{children}</span>
        )}
      </button>
    );
  },
);

ButtonComponent.displayName = "ButtonComponent";
export const Button = memo(ButtonComponent);
